/**
 * studio-agent.tsx — Phase 4 Studio agent UI.
 *
 * Renders as a section inside the existing chat drawer (ChatDrawerSlot)
 * rather than as a floating FAB: Studio already has a dedicated right-side
 * drawer for chat, and the plan requires inline confirmation within that
 * drawer. The component:
 *
 *   1. Reads the WebMCP tool registry via `<WebMCPProvider>`
 *   2. Owns its own composer + transcript (separate from the assistant-ui
 *      Thread — the two are parallel channels for now; unification is
 *      tracked in the Phase 4 PR "out of scope" notes)
 *   3. Sends user text to the configured LlmAdapter
 *   4. Auto-executes read-only proposals
 *   5. Renders an Approve/Reject card for write proposals with a busy
 *      race-guard (setBusy+try-finally — cogo-starter Phase B pattern)
 *
 * All icons are inline SVG (no emoji, no lucide — starter idiomatic).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useWebMCPContext, type ToolManifestEntry } from '@cogo/webmcp-runtime/react'
import {
  STUDIO_AGENT_CONFIG,
  type AgentConfig,
  type ProposedToolCall,
} from './agent-config'
import { buildPageContext, formatContextInstructions } from '@/features/chat/services/chat-context'
import { useStudioStore } from '@/store/studio-store'

// ── Transcript model ──────────────────────────────────────

type TranscriptEntry =
  | { id: string; kind: 'user'; text: string }
  | { id: string; kind: 'reply'; text: string }
  | {
      id: string
      kind: 'tool'
      toolName: string
      status: 'pending' | 'running' | 'ok' | 'error' | 'rejected'
      args: Record<string, unknown>
      result?: unknown
      error?: string
    }

interface StudioAgentProps {
  config?: AgentConfig
  /** Start with the transcript expanded — used by tests. */
  defaultExpanded?: boolean
}

// ── Icons (inline SVG) ────────────────────────────────────

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
      <path d="M20 3v4" />
      <path d="M22 5h-4" />
    </svg>
  )
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 2 11 13" />
      <path d="m22 2-7 20-4-9-9-4Z" />
    </svg>
  )
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    </svg>
  )
}

function AlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  )
}

function ChevronIcon({ className, open }: { className?: string; open?: boolean }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 150ms' }}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  )
}

// ── Main component ─────────────────────────────────────────

export function StudioAgent({ config = STUDIO_AGENT_CONFIG, defaultExpanded = false }: StudioAgentProps) {
  const { registry } = useWebMCPContext()
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([])
  const [toolCount, setToolCount] = useState(registry.size)
  const [pending, setPending] = useState<ProposedToolCall | null>(null)
  const transcriptEndRef = useRef<HTMLDivElement | null>(null)

  // Reactive tool-count + initial sync
  useEffect(() => {
    const unsub = registry.onToolsChanged((tools) => setToolCount(tools.length))
    setToolCount(registry.size)
    return unsub
  }, [registry])

  useEffect(() => {
    const el = transcriptEndRef.current
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'end' })
    }
  }, [transcript, pending])

  const append = useCallback((entry: TranscriptEntry) => {
    setTranscript((prev) => [...prev, entry])
  }, [])

  const setEntryStatus = useCallback(
    (
      id: string,
      patch: Partial<Extract<TranscriptEntry, { kind: 'tool' }>>,
    ) => {
      setTranscript((prev) =>
        prev.map((e) => (e.id === id && e.kind === 'tool' ? { ...e, ...patch } : e)),
      )
    },
    [],
  )

  const executeCall = useCallback(
    async (call: ProposedToolCall) => {
      const entryId = `tool-${call.id}`
      append({
        id: entryId,
        kind: 'tool',
        toolName: call.tool.name,
        status: 'running',
        args: call.args,
      })
      try {
        const result = await registry.callTool(call.tool.name, call.args)
        setEntryStatus(entryId, { status: 'ok', result })
      } catch (err) {
        setEntryStatus(entryId, {
          status: 'error',
          error: err instanceof Error ? err.message : String(err),
        })
      }
    },
    [append, registry, setEntryStatus],
  )

  // Race guard — setBusy + try-finally so double-click on Approve can't
  // dispatch the handler twice. Mirrors cogo-starter Phase B fix.
  const approvePending = useCallback(async () => {
    if (!pending) return
    if (busy) return
    const call = pending
    setPending(null)
    setBusy(true)
    try {
      await executeCall(call)
    } finally {
      setBusy(false)
    }
  }, [pending, busy, executeCall])

  const rejectPending = useCallback(() => {
    if (!pending) return
    append({
      id: `tool-${pending.id}`,
      kind: 'tool',
      toolName: pending.tool.name,
      status: 'rejected',
      args: pending.args,
    })
    setPending(null)
  }, [pending, append])

  const handleSend = useCallback(async () => {
    const text = input.trim()
    if (!text || busy || pending) return
    setInput('')
    setBusy(true)

    append({ id: `user-${Date.now()}`, kind: 'user', text })

    try {
      const manifest = registry.getManifest() as ToolManifestEntry[]
      // Phase 4 IA-4 — inject current shell/route/project/block as the
      // system instruction prefix so the model knows the user's context.
      const ctx = buildPageContext(useStudioStore.getState())
      const systemInstructions = formatContextInstructions(ctx)
      const { reply, toolCalls } = await config.llmAdapter.sendMessage({
        userMessage: text,
        tools: manifest,
        systemInstructions,
      })
      if (reply) append({ id: `reply-${Date.now()}`, kind: 'reply', text: reply })

      for (const call of toolCalls) {
        const verdict = config.confirmationPolicy(call)
        if (verdict === 'confirm') {
          setPending(call)
          return
        }
        await executeCall(call)
      }
    } catch (err) {
      append({
        id: `reply-${Date.now()}`,
        kind: 'reply',
        text: `Adapter error: ${err instanceof Error ? err.message : String(err)}`,
      })
    } finally {
      setBusy(false)
    }
  }, [input, busy, pending, append, registry, config, executeCall])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        void handleSend()
      }
    },
    [handleSend],
  )

  const personaLabel = useMemo(() => config.persona.toUpperCase(), [config.persona])

  return (
    <div
      className="border-b border-border bg-muted/30"
      data-testid="studio-agent-section"
    >
      {/* Header — always visible, click to toggle body. Phase 6 + hotfix:
          padding aligned with TopBar + DashboardHeader (px-6 py-3,
          border-b border-border/40) so the three shell headers all share
          the same visible row height and horizontal rhythm. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-3 px-6 py-3 text-left border-b border-border/40 hover:bg-muted/60"
        data-testid="studio-agent-toggle"
        aria-expanded={expanded}
      >
        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
          <SparkleIcon className="h-3 w-3" />
          {personaLabel}
        </span>
        <span className="text-[11px] text-muted-foreground" data-testid="studio-agent-tool-count">
          {toolCount} {toolCount === 1 ? 'tool' : 'tools'} · {config.llmAdapter.id}
        </span>
        {pending && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-600">
            <AlertIcon className="h-3 w-3" />
            awaiting approval
          </span>
        )}
        {!pending && (
          <ChevronIcon className="ml-auto h-3.5 w-3.5 text-muted-foreground" open={expanded} />
        )}
      </button>

      {(expanded || pending) && (
        <div className="px-3 pb-3">
          {/* Transcript */}
          {(transcript.length > 0 || pending) && (
            <div className="max-h-56 space-y-2 overflow-y-auto rounded-md border border-border bg-background p-2 text-xs">
              {transcript.map((e) =>
                e.kind === 'user' ? (
                  <div key={e.id} className="flex justify-end">
                    <div className="max-w-[85%] rounded-md bg-primary px-2 py-1 text-primary-foreground">{e.text}</div>
                  </div>
                ) : e.kind === 'reply' ? (
                  <div key={e.id} className="max-w-[95%] rounded-md bg-muted px-2 py-1">
                    {e.text}
                  </div>
                ) : (
                  <div key={e.id} className="rounded border border-border bg-card p-2">
                    <div className="flex items-center gap-2">
                      <ShieldIcon className="h-3 w-3 text-muted-foreground" />
                      <span className="font-mono font-semibold">{e.toolName}</span>
                      <ToolStatusBadge status={e.status} />
                    </div>
                    {Object.keys(e.args).length > 0 && (
                      <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all text-[10px] text-muted-foreground">
                        {JSON.stringify(e.args, null, 2)}
                      </pre>
                    )}
                    {e.status === 'error' && e.error && (
                      <p className="mt-1 text-[10px] text-destructive">{e.error}</p>
                    )}
                    {e.status === 'ok' && e.result !== undefined && (
                      <p className="mt-1 text-[10px] text-muted-foreground" data-testid="studio-agent-tool-result">
                        {formatToolResult(e.result)}
                      </p>
                    )}
                  </div>
                ),
              )}

              {pending && (
                <div
                  role="alert"
                  data-testid="studio-agent-confirmation"
                  className="rounded-md border border-amber-500/40 bg-amber-50/60 p-2 dark:bg-amber-900/20"
                >
                  <div className="flex items-center gap-2 text-xs font-medium">
                    <AlertIcon className="h-3.5 w-3.5 text-amber-600" />
                    Confirm action
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    The agent wants to call{' '}
                    <span className="font-mono text-foreground">{pending.tool.name}</span>. Review and approve.
                  </p>
                  {Object.keys(pending.args).length > 0 && (
                    <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all rounded bg-background p-2 text-[10px]">
                      {JSON.stringify(pending.args, null, 2)}
                    </pre>
                  )}
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      data-testid="studio-agent-approve"
                      disabled={busy}
                      onClick={approvePending}
                      className="rounded-md bg-primary px-2 py-1 text-[11px] font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      data-testid="studio-agent-reject"
                      disabled={busy}
                      onClick={rejectPending}
                      className="rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              )}
              <div ref={transcriptEndRef} />
            </div>
          )}

          {/* Composer */}
          <div className="mt-2 flex gap-2">
            <textarea
              data-testid="studio-agent-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={busy || !!pending}
              rows={1}
              placeholder={pending ? 'Resolve the pending action first…' : 'Ask the agent to navigate or act…'}
              className="flex-1 resize-none rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-primary disabled:opacity-50"
            />
            <button
              type="button"
              data-testid="studio-agent-send"
              onClick={handleSend}
              disabled={busy || !!pending || !input.trim()}
              aria-label="Send agent message"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <SendIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Status badge ──────────────────────────────────────────

function ToolStatusBadge({ status }: { status: 'pending' | 'running' | 'ok' | 'error' | 'rejected' }) {
  const map: Record<typeof status, { label: string; cls: string }> = {
    pending: { label: 'pending', cls: 'bg-muted text-muted-foreground' },
    running: { label: 'running', cls: 'bg-blue-500/10 text-blue-600' },
    ok: { label: 'ok', cls: 'bg-success/10 text-success' },
    error: { label: 'error', cls: 'bg-destructive/10 text-destructive' },
    rejected: { label: 'rejected', cls: 'bg-amber-500/10 text-amber-600' },
  }
  const { label, cls } = map[status]
  return (
    <span className={`ml-auto rounded-full px-1.5 py-0 text-[9px] font-medium ${cls}`}>{label}</span>
  )
}

function formatToolResult(result: unknown): string {
  if (!result || typeof result !== 'object' || Array.isArray(result)) {
    return result == null ? 'Done.' : String(result)
  }

  const record = result as Record<string, unknown>
  if (record.ok === true && typeof record.projectId === 'string') {
    const name = typeof record.projectName === 'string' ? record.projectName : 'Project'
    const route = typeof record.route === 'string' ? record.route : `/ide/${record.projectId}`
    const bundle = record.bundle as Record<string, unknown> | undefined
    const pages = typeof bundle?.pages === 'number' ? `${bundle.pages} pages` : null
    const flows = readNestedNumber(record, ['summary', 'flows']) ?? readNestedNumber(record, ['bundle', 'flows'])
    const parts = [`Created ${name}`, `opened ${route}`]
    if (pages) parts.push(pages)
    if (flows != null) parts.push(`${flows} flows`)
    return parts.join(' · ')
  }

  if (typeof record.message === 'string') return record.message
  if (record.ok === true) return 'Done.'
  return JSON.stringify(result)
}

function readNestedNumber(record: Record<string, unknown>, path: string[]): number | null {
  let current: unknown = record
  for (const key of path) {
    if (!current || typeof current !== 'object' || Array.isArray(current)) return null
    current = (current as Record<string, unknown>)[key]
  }
  return typeof current === 'number' ? current : null
}
